<template>
  <div class="legacy-redis-panel">
    <section class="workspace-card legacy-hero">
      <div class="legacy-hero-main">
        <div>
          <div class="legacy-eyebrow">MAZDA · Redis DB1</div>
          <h2>日志及其他数据</h2>
          <p>集中维护日志、未匹配记录、离线控制和每日计数等 Redis Key。大 Hash 已改为分批读取，避免一次性拉取导致超时。</p>
        </div>
        <div class="legacy-hero-actions">
          <el-button :loading="legacyLoading" @click="refreshLegacyAll">刷新</el-button>
          <el-button type="primary" :disabled="!legacyAllowAdd" @click="openLegacyAdd">新增数据</el-button>
        </div>
      </div>

      <div class="legacy-hero-stats">
        <div class="legacy-stat-card">
          <span>Redis 库</span>
          <strong>DB1 / mazda</strong>
        </div>
        <div class="legacy-stat-card">
          <span>当前分类</span>
          <strong>{{ selectedLegacyTarget?.title || '-' }}</strong>
        </div>
        <div class="legacy-stat-card">
          <span>当前结果</span>
          <strong v-if="legacyExactTotal">{{ legacyTotal }} 条</strong>
          <strong v-else>{{ legacyItems.length }} 条 / 当前批</strong>
        </div>
      </div>
    </section>

    <el-card class="workspace-card legacy-toolbar-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>维护分类与查询</span>
          <el-tag v-if="legacyScanMode" type="success" effect="light">HSCAN 分批模式</el-tag>
        </div>
      </template>

      <div class="legacy-target-cards">
        <button
          v-for="item in legacyTargetOptions"
          :key="item.value"
          type="button"
          class="legacy-target-card"
          :class="{ active: item.value === legacyTargetId }"
          @click="selectLegacyTarget(item.value)"
        >
          <span class="legacy-target-card-title">{{ item.label }}</span>
          <span class="legacy-target-card-meta">{{ item.group }} · {{ item.redisType }}</span>
          <strong>{{ item.count ?? 0 }}</strong>
        </button>
      </div>

      <div class="legacy-toolbar">
        <el-select
          v-model="legacyTargetId"
          filterable
          class="legacy-target-select"
          placeholder="选择日志及其他数据分类"
          @change="handleLegacyTargetChange"
        >
          <el-option
            v-for="item in legacyTargetOptions"
            :key="item.value"
            :label="`${item.label}（${item.count}）`"
            :value="item.value"
          >
            <div class="legacy-option-row">
              <span>{{ item.label }}</span>
              <small>{{ item.group }} · {{ item.count }} 条</small>
            </div>
          </el-option>
        </el-select>

        <el-input
          v-model="legacyKeyword"
          clearable
          class="legacy-search-input"
          placeholder="搜索 Key / Field / 内容"
          @keyup.enter="handleLegacySearch"
          @clear="handleLegacySearch"
        />

        <el-checkbox
          v-model="legacyFieldExact"
          class="legacy-field-exact"
          :disabled="!legacyCanUseFieldExact || !legacyKeyword"
          @change="handleLegacySearch"
        >
          精确 Field
        </el-checkbox>

        <el-button type="primary" :loading="legacyLoading" @click="handleLegacySearch">查询</el-button>
      </div>

      <div class="legacy-summary-strip">
        <div class="legacy-summary-item legacy-summary-wide">
          <span>数据用途</span>
          <strong>{{ selectedLegacyTarget?.description || '-' }}</strong>
        </div>
        <div class="legacy-summary-item">
          <span>Key / Pattern</span>
          <strong>{{ selectedLegacyTarget?.key || selectedLegacyTarget?.pattern || '-' }}</strong>
        </div>
      </div>

      <el-alert
        v-if="legacyCanUseFieldExact"
        class="legacy-tip-alert"
        title="OFFlineID、filename_find 等 Hash 不再使用 HGETALL，全量查看改为 HSCAN 分批读取；查询单个文件名时建议输入完整 Field 并勾选“精确 Field”。"
        type="info"
        :closable="false"
        show-icon
      />
      <el-alert
        v-if="legacyNotice"
        class="legacy-tip-alert"
        :title="legacyNotice"
        type="warning"
        :closable="false"
        show-icon
      />
    </el-card>

    <el-card class="workspace-card legacy-list-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>DB1 数据列表</span>
          <div class="legacy-total-text">
            <span v-if="legacyExactTotal">共 {{ legacyTotal }} 条</span>
            <span v-else>当前批 {{ legacyItems.length }} 条</span>
          </div>
        </div>
      </template>

      <div class="table-scroll-shell">
        <div class="table-scroll-inner legacy-table-scroll-inner">
          <el-table
            :data="legacyItems"
            stripe
            border
            class="result-table legacy-result-table"
            v-loading="legacyLoading"
            empty-text="暂无日志及其他数据"
            row-key="rowId"
          >
            <el-table-column prop="targetTitle" label="分类" min-width="160" show-overflow-tooltip />
            <el-table-column prop="fullKey" label="完整 Key" min-width="300" show-overflow-tooltip />
            <el-table-column label="类型" width="95" align="center">
              <template #default="scope">
                <el-tag size="small" effect="light">{{ scope.row.valueType || scope.row.redisType || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Field / Member / Index" min-width="210" show-overflow-tooltip>
              <template #default="scope">
                <code class="legacy-inline-code">{{ scope.row.field || scope.row.member || (scope.row.index ?? '-') }}</code>
              </template>
            </el-table-column>
            <el-table-column label="Key 内数量" width="110" align="right">
              <template #default="scope">
                <span class="legacy-count-pill">{{ scope.row.count ?? '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="内容预览" min-width="380" show-overflow-tooltip>
              <template #default="scope">
                <span class="legacy-preview-text">{{ scope.row.valuePreview || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="190" align="center" class-name="action-column">
              <template #default="scope">
                <el-button
                  size="small"
                  type="primary"
                  @click="scope.row.editable === false ? openLegacyView(scope.row) : openLegacyEdit(scope.row)"
                >{{ scope.row.editable === false ? '查看' : '编辑' }}</el-button>
                <el-button
                  size="small"
                  type="danger"
                  plain
                  :loading="legacySaving"
                  @click="deleteLegacyRow(scope.row)"
                >{{ scope.row.valueType === 'list' ? '删Key' : '删除' }}</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="legacyScanMode" class="scan-pagination-wrap">
            <div class="scan-pagination-info">
              <strong>{{ legacyPagerLabel }}</strong>
              <span v-if="legacyExactTotal">总数来自 HLEN，列表按 HSCAN 分批读取。</span>
              <span v-else>模糊搜索为分批结果，不再全量统计总数。</span>
            </div>
            <div class="scan-pagination-actions">
              <span class="pager-label">每批约</span>
              <el-select v-model="legacyPageSize" class="page-size-select" @change="handleLegacySizeChange">
                <el-option :value="20" label="20 条" />
                <el-option :value="50" label="50 条" />
                <el-option :value="100" label="100 条" />
                <el-option :value="200" label="200 条" />
              </el-select>
              <el-button :disabled="legacyCursorHistory.length === 0 || legacyLoading" @click="handleLegacyPrevBatch">上一批</el-button>
              <el-button type="primary" :disabled="!legacyHasMore || legacyLoading" :loading="legacyLoading" @click="handleLegacyNextBatch">下一批</el-button>
            </div>
          </div>

          <div v-else class="pagination-wrap legacy-pagination-wrap">
            <div class="legacy-pagination-left">{{ legacyPagerLabel }}</div>
            <div class="legacy-pagination-right">
              <span class="pager-label">每页</span>
              <el-select v-model="legacyPageSize" class="page-size-select" @change="handleLegacySizeChange">
                <el-option :value="20" label="20 条" />
                <el-option :value="50" label="50 条" />
                <el-option :value="100" label="100 条" />
                <el-option :value="200" label="200 条" />
              </el-select>
              <el-pagination
                background
                layout="prev, pager, next"
                :total="legacyTotal"
                :current-page="legacyPage"
                :page-size="legacyPageSize"
                @current-change="handleLegacyPageChange"
              />
              <span class="pager-label">跳至</span>
              <el-input v-model="legacyJumpPage" class="jump-input" @keyup.enter="handleLegacyJump" />
              <span class="pager-label">页</span>
              <el-button @click="handleLegacyJump">确定</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <el-dialog
      v-model="legacyDialogVisible"
      :title="legacyDialogTitle"
      width="780px"
    >
      <div class="legacy-edit-form">
        <el-form label-width="150px">
          <el-form-item label="完整 Key">
            <el-input
              v-model="legacyForm.fullKey"
              :readonly="legacyReadOnly || legacyFullKeyReadonly"
              placeholder="例如 filename_find"
            />
          </el-form-item>

          <el-form-item label="数据类型">
            <el-select v-model="legacyForm.valueType" :disabled="legacyReadOnly || legacyEditMode === 'edit'" class="legacy-type-select">
              <el-option v-for="item in legacyValueTypeOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>

          <el-form-item v-if="legacyForm.valueType === 'hash'" label="Hash Field">
            <el-input v-model="legacyForm.field" :readonly="legacyReadOnly || legacyFieldReadonly" placeholder="请输入 Hash field" />
          </el-form-item>

          <el-form-item v-if="legacyForm.valueType === 'set'" label="Set Member">
            <el-input v-model="legacyForm.member" :readonly="legacyReadOnly" placeholder="请输入 Set member" />
          </el-form-item>

          <el-form-item v-if="legacyForm.valueType !== 'set'" label="内容">
            <el-input
              v-model="legacyForm.value"
              type="textarea"
              :rows="legacyReadOnly ? 18 : 12"
              resize="none"
              class="legacy-textarea"
              :readonly="legacyReadOnly"
              placeholder="请输入要保存的内容"
            />
          </el-form-item>

          <el-alert
            v-if="legacyForm.valueType === 'list'"
            title="List 日志只提供查看和删除整个 Key，不支持逐条修改。"
            type="warning"
            :closable="false"
            show-icon
          />
        </el-form>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button :disabled="legacySaving" @click="legacyDialogVisible = false">{{ legacyReadOnly ? '关闭' : '取消' }}</el-button>
          <el-button v-if="!legacyReadOnly" type="primary" :disabled="!legacyCanSave" :loading="legacySaving" @click="submitLegacySave">保存</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { useMazdaLegacyRedis } from '@/composables/mazda/useMazdaLegacyRedis';

const props = defineProps({
  serverName: { type: String, required: true },
  mazdaPost: { type: Function, required: true },
});

const {
  legacyTargetId,
  legacyTargetOptions,
  selectedLegacyTarget,
  legacyItems,
  legacyTotal,
  legacyPage,
  legacyPageSize,
  legacyKeyword,
  legacyJumpPage,
  legacyCursorHistory,
  legacyHasMore,
  legacyScanMode,
  legacyExactTotal,
  legacyNotice,
  legacyFieldExact,
  legacyCanUseFieldExact,
  legacyPagerLabel,
  legacyLoading,
  legacySaving,
  legacyReadOnly,
  legacyDialogTitle,
  legacyDialogVisible,
  legacyEditMode,
  legacyForm,
  legacyAllowAdd,
  legacyValueTypeOptions,
  legacyCanSave,
  legacyFullKeyReadonly,
  legacyFieldReadonly,
  refreshLegacyAll,
  handleLegacyTargetChange,
  selectLegacyTarget,
  handleLegacySearch,
  handleLegacyPageChange,
  handleLegacySizeChange,
  handleLegacyJump,
  handleLegacyNextBatch,
  handleLegacyPrevBatch,
  openLegacyAdd,
  openLegacyView,
  openLegacyEdit,
  submitLegacySave,
  deleteLegacyRow,
} = useMazdaLegacyRedis(props.mazdaPost, toRef(props, 'serverName'));
</script>

<style scoped>
.legacy-redis-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.workspace-card {
  border-radius: 24px;
  border: 1px solid rgba(225, 232, 242, 0.95);
  box-shadow: 0 12px 30px rgba(29, 46, 76, 0.07);
  background: #fff;
}

.workspace-card :deep(.el-card__body) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.legacy-hero {
  padding: 22px 24px;
  background:
    radial-gradient(circle at top left, rgba(64, 158, 255, 0.16), transparent 36%),
    linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%);
}

.legacy-hero-main,
.card-header,
.header-actions,
.legacy-toolbar,
.legacy-pagination-right,
.scan-pagination-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legacy-hero-main,
.card-header {
  justify-content: space-between;
}

.legacy-eyebrow {
  color: #409eff;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.legacy-hero h2 {
  margin: 6px 0 8px;
  color: #172033;
  font-size: 24px;
  line-height: 1.25;
}

.legacy-hero p {
  margin: 0;
  max-width: 780px;
  color: #667085;
  line-height: 1.65;
}

.legacy-hero-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.legacy-hero-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.legacy-stat-card,
.legacy-summary-item,
.legacy-target-card {
  border: 1px solid #e8eef8;
  background: rgba(255, 255, 255, 0.86);
  border-radius: 18px;
}

.legacy-stat-card {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.legacy-stat-card span,
.legacy-summary-item span,
.legacy-target-card-meta {
  color: #8a94a6;
  font-size: 12px;
}

.legacy-stat-card strong,
.legacy-summary-item strong {
  color: #252f3f;
  word-break: break-all;
}

.card-header {
  gap: 16px;
  font-weight: 800;
  color: #1f2d3d;
}

.legacy-target-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.legacy-target-card {
  position: relative;
  min-height: 92px;
  padding: 14px 14px 12px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
}

.legacy-target-card:hover,
.legacy-target-card.active {
  transform: translateY(-1px);
  border-color: rgba(64, 158, 255, 0.55);
  box-shadow: 0 10px 22px rgba(64, 158, 255, 0.12);
}

.legacy-target-card.active::after {
  content: "";
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 0;
  height: 3px;
  border-radius: 999px 999px 0 0;
  background: #409eff;
}

.legacy-target-card-title,
.legacy-target-card strong {
  display: block;
}

.legacy-target-card-title {
  color: #1f2d3d;
  font-weight: 800;
  line-height: 1.35;
}

.legacy-target-card-meta {
  display: block;
  margin-top: 6px;
}

.legacy-target-card strong {
  margin-top: 10px;
  color: #409eff;
  font-size: 20px;
}

.legacy-toolbar,
.legacy-pagination-right,
.scan-pagination-actions {
  flex-wrap: wrap;
}

.legacy-toolbar {
  padding: 14px;
  border: 1px solid #edf2fb;
  border-radius: 18px;
  background: #fbfcff;
}

.legacy-target-select {
  width: 320px;
}

.legacy-search-input {
  width: 340px;
}

.legacy-field-exact {
  padding: 0 8px;
  white-space: nowrap;
}

.legacy-option-row {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.legacy-option-row small {
  color: #909399;
}

.legacy-summary-strip {
  margin-top: 14px;
  display: grid;
  grid-template-columns: minmax(360px, 1.5fr) minmax(240px, 0.9fr);
  gap: 12px;
}

.legacy-summary-item {
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: linear-gradient(180deg, #fbfcff, #f7faff);
}

.legacy-tip-alert {
  margin-top: 12px;
  border-radius: 14px;
}

.legacy-total-text,
.legacy-pagination-left,
.pager-label {
  color: #606266;
  font-weight: 600;
}

.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 4px;
}

.table-scroll-inner {
  min-width: 100%;
}

.legacy-table-scroll-inner {
  min-width: 1320px;
}

.result-table {
  width: 100%;
}

.legacy-result-table :deep(.el-table__header th) {
  background: #f7f9fc;
  color: #475467;
  font-weight: 800;
}

.result-table :deep(.el-table__inner-wrapper::before) {
  height: 0;
}

.legacy-inline-code {
  padding: 2px 6px;
  border-radius: 8px;
  background: #f4f6fb;
  color: #344054;
  font-family: Consolas, "Courier New", monospace;
}

.legacy-count-pill {
  display: inline-flex;
  min-width: 34px;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5ff;
  color: #3d5a9d;
  font-weight: 700;
}

.legacy-preview-text {
  color: #475467;
}

.pagination-wrap,
.legacy-pagination-wrap,
.scan-pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 18px;
}

.scan-pagination-wrap {
  padding: 14px;
  border: 1px solid #edf2fb;
  border-radius: 18px;
  background: #fbfcff;
}

.scan-pagination-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #667085;
}

.scan-pagination-info strong {
  color: #1f2d3d;
}

.page-size-select {
  width: 110px;
}

.jump-input {
  width: 72px;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.legacy-type-select {
  width: 220px;
}

.legacy-textarea :deep(.el-textarea__inner) {
  font-family: Consolas, "Courier New", monospace;
  line-height: 1.65;
  border-radius: 18px;
}

:deep(.action-column .cell) {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
  padding-left: 8px;
  padding-right: 8px;
}

@media (max-width: 1180px) {
  .legacy-hero-main,
  .card-header,
  .pagination-wrap,
  .scan-pagination-wrap {
    align-items: stretch;
    flex-direction: column;
  }

  .legacy-hero-stats,
  .legacy-summary-strip {
    grid-template-columns: 1fr;
  }

  .legacy-target-select,
  .legacy-search-input {
    width: 100%;
  }

  .legacy-toolbar,
  .legacy-pagination-right,
  .scan-pagination-actions {
    align-items: stretch;
  }
}
</style>
